import { useQueryClient } from "@tanstack/react-query";
import {
  getInvestmentEntityStatsRetrieveQueryKey,
  getInvestmentTableDataRetrieveQueryKey,
  useInvestmentDestroy,
} from "gen/endpoints/investment/investment";
import { useTranslation } from "react-i18next";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export interface InvestmentConfirmDeleteArgs {
  investmentId: string;
}

export const useInvestmentConfirmDelete = ({
  fundFamilyId,
  isPortfolio,
  onSuccess,
}: {
  fundFamilyId: string;
  isPortfolio?: boolean;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation(["investment"]);
  const queryClient = useQueryClient();

  const { mutateAsync: deleteInvestment } = useInvestmentDestroy({
    mutation: {
      onSuccess: async () => {
        await invalidateQueryKeys(
          queryClient,
          getInvestmentTableDataRetrieveQueryKey(fundFamilyId),
          getInvestmentEntityStatsRetrieveQueryKey(fundFamilyId),
        );
        onSuccess?.();
      },
    },
  });

  return useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<InvestmentConfirmDeleteArgs>,
    {
      title: isPortfolio
        ? t("investment:delete_portfolio.title")
        : t("investment:delete.title"),
      description: t("investment:delete.description"),
      successMessage: isPortfolio
        ? t("investment:delete_portfolio.success")
        : t("investment:delete.success"),
      performDelete: ({ investmentId }) =>
        deleteInvestment({ fundFamilyId, id: investmentId }),
    },
  );
};
